// =========================================================
// Vojjin Hash Utilities
// =========================================================
import CryptoJS from "crypto-js";
import {registerGlobal} from "./core.js";

class Hash {

    /**
     * Computes the MD5 hash of the provided input string.
     *
     * This function takes a string input, processes it using the MD5 hashing algorithm,
     * and returns the resulting hash as a hexadecimal string.
     *
     * @param {string} input - The string to be hashed.
     * @returns {string} The MD5 hash of the input string, represented as a hexadecimal string.
     */
    static md5 = (input) => {
        return CryptoJS.MD5(input).toString(CryptoJS.enc.Hex);
    };

    /**
     * Computes the SHA-1 hash of the given input and returns it as a hexadecimal string.
     *
     * This function uses the CryptoJS library to generate the SHA-1 hash.
     *
     * @param {string} input - The input string to be hashed.
     * @returns {string} The resulting SHA-1 hash as a hexadecimal string.
     */
    static sha1 = (input) => {
        return CryptoJS.SHA1(input).toString(CryptoJS.enc.Hex);
    };

    /**
     * Computes the SHA-256 hash of a given input string.
     *
     * The function takes an input string, processes it using the SHA-256 hashing algorithm,
     * and returns the resulting hash as a hexadecimal string.
     *
     * @param {string} input - The input string to be hashed.
     * @returns {string} The SHA-256 hash of the input, represented as a hexadecimal string.
     */
    static sha256 = (input) => {
        return CryptoJS.SHA256(input).toString(CryptoJS.enc.Hex);
    };

    /**
     * Encodes a given input string into Base64 format.
     *
     * This function accepts a string, converts it into its UTF-8 encoded binary format,
     * and then encodes the binary data into a Base64 representation. If the encoding fails,
     * it logs an error and returns null.
     *
     * Potential use cases include encoding data for transport or storage
     * in Base64-compatible systems or protocols.
     *
     * @param {string} input - The input string to be encoded into Base64.
     * @returns {string|null} The Base64 encoded string or null if the encoding fails.
     */
    static base64Encode = (input) => {
        try {
            const utf8 = new TextEncoder().encode(input);
            let binary = "";
            utf8.forEach(b => (binary += String.fromCharCode(b)));
            return btoa(binary);
        } catch (err) {
            Logger.error("Base64 encode failed:", err);
            return null;
        }
    };

    /**
     * Decodes a Base64-encoded string into its original UTF-8 text representation.
     *
     * This function takes a string encoded in Base64 format, decodes it into binary data,
     * converts it to a Uint8Array, and then decodes it back to a UTF-8 string.
     *
     * If the input string is not properly Base64-encoded, an error is logged, and the function
     * returns `null`.
     *
     * @param {string} encodedString - The Base64-encoded input string to be decoded.
     * @returns {string | null} The decoded UTF-8 string if successful, or `null` if decoding fails.
     */
    static base64Decode = (encodedString) => {
        try {
            const binary = atob(encodedString);
            const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
            return new TextDecoder().decode(bytes);
        } catch (err) {
            Logger.error("Base64 decode failed:", err);
            return null;
        }
    };

}

registerGlobal("Hash", Hash);

